import { ref, onBeforeUnmount, onMounted } from 'vue';

// Chrome (and others) throttle setTimeout/setInterval heavily in backgrounded
// tabs, so a poll scheduled while a tab is hidden can stall well past its
// nominal interval. Re-running the callback immediately when the tab becomes
// visible again means a tab left open across a deploy/config-change self-heals
// on the next glance instead of silently showing stale state indefinitely.
export const useLiveRefresh = (callback, interval = 60000) => {
  const timeoutId = ref(null);

  const startRefetching = () => {
    timeoutId.value = setTimeout(async () => {
      await callback();
      startRefetching();
    }, interval);
  };

  const stopRefetching = () => {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value);
      timeoutId.value = null;
    }
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    stopRefetching();
    callback().finally(startRefetching);
  };

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });

  onBeforeUnmount(() => {
    stopRefetching();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });

  return {
    startRefetching,
    stopRefetching,
  };
};
